import React from 'react';

import { Form, Modal } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from 'segments/hooks';
import { useNotification } from 'segments/hooks/useNotification';

import { InputStr, SubmiteButton, WeekPicker } from 'shared/FormItems';
import { ModalFooter } from 'shared/Modals';

import { SelectBookingTime } from 'features/bookingTimeSelector';
import { CoachesSelector } from 'features/coaches';
import { bookingType, CreateCourtBookingForm, selectActiveBookingCourt, SelectBookingType, useCreateBooking } from 'features/courtsBooking';
import { SelectGroupe } from 'features/groups';
import { ProductsSelector } from 'features/products/components/ProductsSelector';
import { SelectUsers } from 'features/users';

type FormData = CreateCourtBookingForm
interface Props {
  handleCancel: () => void;
}

export const BookingCourtModal = ({ handleCancel }: Props) => {
  const { t } = useTranslation();

  const initData = useAppSelector(selectActiveBookingCourt);
  const [form] = Form.useForm<FormData>();
  const formBookingType = bookingType[Form.useWatch('bookingType', form) ?? 0];
  const bookingStartTime = dayjs(initData?.startTime);
  const { createBooking, isSuccess, isError, isLoading, error } = useCreateBooking({ courtId: initData?.courtId, bookingStartTime });

  useNotification({
    isSuccess,
    isError,
    onSuccess: () => handleCancel(),
    onError: () => handleCancel(),
    error
  });

  if (initData === null) {
    return;
  }

  const modalTittle =
    `${t('courts.title.booking')} ${initData.courtName} ${t('from')} ${bookingStartTime.format('HH:mm')}`;
  return (
    <Modal
      title={modalTittle}
      open
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <ModalFooter
          key="modalFooter"
          handleCancel={handleCancel}
          resetFields={form.resetFields}
        />
      ]}
    >
      <Form
        name="basic"
        layout="vertical"
        form={form}
        initialValues={{
          ...initData,
          startTime: bookingStartTime,
          endTime: bookingStartTime?.add(1, 'hour'),
          bookingType: 0
        }}
        onFinish={createBooking}
        autoComplete="off"
        className="edit-user__form"
      >
        <SelectBookingTime form={form} />
        <WeekPicker
          startWeek={bookingStartTime}
          formItemsProps={{
            name: 'repeatUntil',
            label: t('form-items.select.repeat-until-date')
          }}
        />
        <SelectBookingType />
        <InputStr type='note' />
        {
          formBookingType === 'groupe' && (
            <SelectGroupe
              formProps={{ rules: [{ required: true, message: 'Необходима выбрать группу' }] }}
            />
          )
        }
        {
          formBookingType !== 'groupe' && (
            <>
              <CoachesSelector oneSelectMode />
              <ProductsSelector<FormData> form={form} />
              <SelectUsers oneSelectMode allowQuickCreate />
            </>
          )
        }
        {
          formBookingType === 'vip-client' && (
            <>
              <InputStr type='customCourtPrice' />
              <InputStr type='customCoachPrice' />
            </>
          )
        }
        <SubmiteButton submiteText={t('table.booking')} isLoading={isLoading} />
      </Form>
    </Modal >
  );
};
